import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { TranslatedToastrService, ValidationHandlerService, EditComponent, BaseComponent, ExcelExportService, LanguageService, VestraUserSearchData, PdfPreviewService, FormatService } from 'lib/Vestra.shared';
import { Location } from '@angular/common';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationService, DataTableRequest, VestraUserService } from 'src/app/services/api-services';
import { HttpErrorResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { ADTSettings } from 'angular-datatables/src/models/settings';
import 'datatables.net';
import 'datatables.net-responsive';

@Component({
    selector: 'app-vestra-user-list',
    templateUrl: './vestra-user-list.component.html',
    styleUrls: ['./vestra-user-list.component.scss'],
})
export class VestraUserListComponent extends BaseComponent implements OnInit, AfterViewInit {
    alphabet: string[] = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

    public propertyName: string = null;
    public propertyValue: string = null;
    public invoiceId: number = null;
    public nameStart: string = null;

    public todayProviderCount: number;
    public last7DaysProviderCount: number;
    public last30DaysProviderCount: number;
    public last12MonthsProviderCount: number;

    public providerCount: number;

    constructor(
        protected override modalService: BsModalService,
        private formatService: FormatService,
        private bsModalRef: BsModalRef,
        private location: Location,
        private router: Router,
        private vestraUserService: VestraUserService,
        protected override validationHandlerService: ValidationHandlerService,
        private translatedToastrService: TranslatedToastrService,
        private authenticationService: AuthenticationService,
        protected override spinnerService: NgxSpinnerService,
        private excelExportService: ExcelExportService,
        protected translateService: TranslateService,
        protected override languageService: LanguageService,
        private pdfPreviewService: PdfPreviewService
    ) {
        super(spinnerService, modalService, validationHandlerService, translateService, languageService);
    }

    public ngOnInit(): void {
        this.dataTableOnInit();
    }

    public ngAfterViewInit() {
        this.dataTableAfterViewInit();
    }

    public ngOnDestroy() {
        this.dataTableOnDestroy();
    }

    public override getDtOptions() {
        this.languageUrl = this.languageService.getTranslationUrl();

        const res: ADTSettings = {
            serverSide: true,
            processing: false,
            pageLength: 20,
            lengthChange: false,
            responsive: {
                details: {
                    type: 'column',
                    target: -1,
                },
            },
            searching: false,
            destroy: true,
            language: {
                url: this.languageUrl,
            },
            ajax: (dataTablesParams: any, callback: any) => {
                const searchData: VestraUserSearchData = {
                    propertyValue: this.propertyValue,
                    propertyName: this.propertyName,
                    nameStart: this.nameStart,
                };

                for (let order of dataTablesParams.order) {
                    order.name = dataTablesParams.columns[order.column]?.data;
                }

                const model: DataTableRequest = {
                    draw: dataTablesParams.draw,
                    start: dataTablesParams.start,
                    length: dataTablesParams.length,
                    order: dataTablesParams.order,
                    search: searchData,
                    columns: this.transformToColumnData(this.dtOptions.columns),
                };

                this.datatableRequest = model;

                this.handleSpinner(firstValueFrom(this.vestraUserService.getVestraUserListDataSource(model)))
                    .then(res => {
                        this.providerCount = res.recordsTotal;
                        callback(this.getTableData(res));
                        return Promise.resolve(res);
                    })
                    .catch((err: HttpErrorResponse) => {
                        this.validationHandlerService.handleServerError(err);
                        callback(this.getEmptyTableData());
                    });
            },
            columns: [
                {
                    data: 'id',
                    title: 'common.id',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'firstName',
                    title: 'vestra_user.first_name',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'lastName',
                    title: 'vestra_user.last_name',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: '',
                    orderable: false,
                    responsivePriority: 1,
                    render: function (data) {
                        return '';
                    },
                },
            ],
            columnDefs: [
                {
                    targets: '_all',
                    visible: true,
                },
                {
                    className: 'details-icon dtr-control',
                    orderable: false,
                    target: -1,
                },
            ],
            rowId: 'fullName',
        };

        return res;
    }

    public onLetterSelect(letter: string) {
        this.nameStart = letter;
        this.refreshDataTable();
    }

    public onEditItem(event: { action: string; data: any }) {
        if (event.action === 'edit') {
            this.navigateToProviderEditPage(event);
        }
    }
    public onExcelExport() {
        this.excelExportService.exportExcel('ProviderModel', this.datatableRequest);
    }

    public navigateToProviderEditPage(event: any) {
        this.router.navigate([`/administration/provider-edit/${event.data.id}`]);
    }

    public clearNameStartSearch() {
        this.nameStart = null;
        this.refreshDataTable();
    }

    public setSearchProperties(event: { propertyName: string; propertyValue: string }) {
        this.propertyName = event.propertyName;
        this.propertyValue = event.propertyValue;
        this.refreshDataTable();
    }

    private getDateRange(type: string): { from: Date; to: Date } {
        const now = new Date();
        let fromDate = new Date();
        let toDate = new Date();

        switch (type) {
            case 'today':
                fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
                break;
            case 'last7Days':
                fromDate.setDate(now.getDate() - 7);
                break;
            case 'last30Days':
                fromDate.setDate(now.getDate() - 30);
                break;
            case 'last12Months':
                fromDate.setFullYear(now.getFullYear() - 1);
                break;
        }

        return { from: fromDate, to: toDate };
    }
}
